import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { isCancel } from 'axios';
import { fetchAssessmentDraft, saveAssessmentDraft, deleteAssessmentDraft } from '../../api/assessmentDrafts';
import { Lookup } from '../../types/lookup';
import { Typography, Button, Snackbar, Alert, AlertTitle, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ThemeBackground from '../Layout/ThemeBackground';
import AvailabilitySlotEditor, { AvailabilitySlot } from '../shared/AvailabilitySlotEditor';
import '../../index.css';

interface ErrorType {
  [key: string]: string;
}

interface ClientNameState {
  firstName: string;
  lastName: string;
  initialAssessment: string;
  referralTypeDisplayName?: string;
}

interface AssessmentState {
  concernsDuration: string;
  mainObjectives: string;
  personalPreferences: string;
  generalConcerns: string;
  phq9Scores: (number | undefined)[];
  gad7Scores: (number | undefined)[];
  phq9Total: number;
  gad7Total: number;
  riskToSelf: string;
  riskToOthers: string;
  previousCounsellor: string;
  officialDiagnoses: string;
  medication: string;
  recreationalDrugs: string;
  alcoholUse: string;
  substanceSupport: string;
  personalityDisorder: string;
  otherSupport: string;
  sessionTypes: string[];
  availability: string[];
  notes: string;
  hasPlan: string;
  planDetails: string;
  difficultyLevelPHQ9: string;
  difficultyLevelGAD7: string;
}

const phq9Questions = [
  "Little interest or pleasure in doing things?",
  "Feeling down, depressed, or hopeless?",
  "Trouble falling or staying asleep, or sleeping too much?",
  "Feeling tired or having little energy?",
  "Poor appetite or overeating?",
  "Feeling bad about yourself — or that you are a failure or have let yourself or your family down?",
  "Trouble concentrating on things, such as reading the newspaper or watching television?",
  "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving a lot more than usual?",
  "Thoughts that you would be better off dead or of hurting yourself in some way?"
];

const gad7Questions = [
  "Feeling nervous, anxious, or on edge?",
  "Not being able to stop or control worrying?",
  "Worrying too much about different things?",
  "Trouble relaxing?",
  "Being so restless that it's hard to sit still?",
  "Becoming easily annoyed or irritable?",
  "Feeling afraid as if something awful might happen?"
];

function AssessmentPart2() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [clientName, setClientName] = useState<ClientNameState>({ 
    firstName: '', 
    lastName: '', 
    initialAssessment: '',
    referralTypeDisplayName: ''
  });
  const [sessionTypes, setSessionTypes] = useState<Lookup[]>([]);
  const [availabilityOptions, setAvailabilityOptions] = useState<Lookup[]>([]);
  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [assessment, setAssessment] = useState<AssessmentState>({
    concernsDuration: '',
    mainObjectives: '',
    personalPreferences: '',
    generalConcerns: '',
    phq9Scores: Array(9).fill(undefined),
    gad7Scores: Array(7).fill(undefined),
    phq9Total: 0,
    gad7Total: 0,
    riskToSelf: '',
    riskToOthers: '',
    previousCounsellor: '',
    officialDiagnoses: '',
    medication: '',
    recreationalDrugs: '',
    alcoholUse: '',
    substanceSupport: '',
    personalityDisorder: '',
    otherSupport: '',
    sessionTypes: [],
    availability: [],
    notes: '',
    hasPlan: '',
    planDetails: '',
    difficultyLevelPHQ9: '',
    difficultyLevelGAD7: ''
  });

  const [errors, setErrors] = useState<ErrorType>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [showRestoreDraftDialog, setShowRestoreDraftDialog] = useState(false);
  const [savedDraft, setSavedDraft] = useState<AssessmentState | null>(null);
  const [showCrisisModal, setShowCrisisModal] = useState(false);
  const [isSendingSignpost, setIsSendingSignpost] = useState(false);
  const [assessorNotes, setAssessorNotes] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const isRestoringDraft = useRef(false);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchData = async () => {
      try {
        console.log('Fetching data...');
        const [clientResponse, sessionTypeResponse, availabilityResponse] = await Promise.all([
          api.get(`/api/clients/${clientId}`, { signal }),
          api.get(`/api/lookups/public/sessionTypes`, { signal }), // Use public endpoint
          api.get(`/api/lookups/public/availability`, { signal })  // Use public endpoint
        ]);

        console.log('Session type response:', sessionTypeResponse.data);

        if (!clientResponse.data || typeof clientResponse.data !== 'object') {
          console.error('Invalid client data:', clientResponse.data);
          throw new Error('Invalid client data received');
        }

        const { 
          firstName, 
          lastName, 
          initialAssessment, 
          referralTypeDisplayName,
          referralCategory,           
          organisationDisplayName,    
          assessment: existingAssessment 
        } = clientResponse.data;
        
        // Set client name and initial assessment
        setClientName({ 
          firstName, 
          lastName, 
          initialAssessment: initialAssessment || '',
          referralTypeDisplayName: referralCategory === 'organisation' ? 
            organisationDisplayName : 
            referralTypeDisplayName
        });
        
        // Filter and set session types
        const filteredSessionTypes = sessionTypeResponse.data
          .filter((type: Lookup) => type.displayName && !type.displayName.toLowerCase().includes('select'));
        console.log('Filtered session types:', filteredSessionTypes);
        setSessionTypes(filteredSessionTypes);

        // Set availability options - sorted by sortOrder
        const sortedAvailability = availabilityResponse.data
          .filter((time: Lookup) => !time.displayName.toLowerCase().includes('select'))
          .sort((a: Lookup, b: Lookup) => (a.sortOrder || 999) - (b.sortOrder || 999));
        setAvailabilityOptions(sortedAvailability);

        // Set existing assessment data if available
        if (existingAssessment) {
          console.log('Setting existing assessment:', existingAssessment);
          setAssessment(prev => ({
            ...prev,
            ...existingAssessment,
            sessionTypes: existingAssessment.sessionTypes || [],
            availability: existingAssessment.availability || []
          }));
          // Load existing availability slots from client data
          if (clientResponse.data.availabilitySlots) {
            setAvailabilitySlots(clientResponse.data.availabilitySlots);
          }
        }

      } catch (error) {
        if (!isCancel(error)) {
          console.error('Error fetching data:', error);
        }
      }
    };

    if (clientId) {
      fetchData();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [clientId]);

  // Phase 2C, Stage 6: drafts are now encrypted and stored server-side
  // (design §9, resolving CLIENT-08), scoped to this assessor and this
  // client - replacing the previous plaintext browser localStorage
  // drafts. Server-side TTL (7 days) and immediate deletion on successful
  // submission are handled by the backend; the UX (restore/discard
  // dialog) is unchanged.

  // Check for a saved draft on mount
  useEffect(() => {
    if (!clientId) return;

    const checkForDraft = async () => {
      try {
        const draft = await fetchAssessmentDraft<AssessmentState>(clientId, 'part2');
        if (draft) {
          setSavedDraft(draft.content);
          setShowRestoreDraftDialog(true);
        }
      } catch (error) {
        console.error('Error checking for draft:', error);
      }
    };

    // Delay check to allow initial data load
    const timer = setTimeout(checkForDraft, 1000);
    return () => clearTimeout(timer);
  }, [clientId]);

  // Auto-save draft to the server
  useEffect(() => {
    if (!clientId || isRestoringDraft.current) return;

    // Don't save if form is empty (initial state)
    const hasData = assessment.generalConcerns.trim() || 
                    assessment.mainObjectives.trim() || 
                    assessment.phq9Scores.some(score => score !== undefined) ||
                    assessment.gad7Scores.some(score => score !== undefined);

    if (!hasData) return;

    // Debounce the save
    const timer = setTimeout(() => {
      saveAssessmentDraft(clientId, 'part2', assessment).catch((error) => {
        console.error('Error saving draft:', error);
      });
    }, 1000); // Save 1 second after user stops typing

    return () => clearTimeout(timer);
  }, [assessment, clientId]);


  const scoreOptions = [
    { value: '', label: 'Please select...' },
    { value: '0', label: 'Not at all' },
    { value: '1', label: 'Several days' },
    { value: '2', label: 'More than half the days' },
    { value: '3', label: 'Nearly every day' },
  ];


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAssessment((prev: AssessmentState) => ({ ...prev, [name]: value }));

    // Clear the error for the updated field
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[name];
      return updatedErrors;
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePhq9Change = (index: number, value: string) => {
    const newPhq9 = [...assessment.phq9Scores];
    // Handle "Not at all" as 0, empty as undefined
    newPhq9[index] = value === '' ? undefined : parseInt(value);
    setAssessment((prev: AssessmentState) => ({ ...prev, phq9Scores: newPhq9 }));

    // Clear error for the specific question if a value is selected
    if (value !== '') {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`phq9_${index}`];
        return updatedErrors;
      });
    }
  };

  const handleGad7Change = (index: number, value: string) => {
    const newGad7 = [...assessment.gad7Scores];
    // Handle "Not at all" as 0, empty as undefined
    newGad7[index] = value === '' ? undefined : parseInt(value);
    setAssessment((prev: AssessmentState) => ({ ...prev, gad7Scores: newGad7 }));

    // Clear error for the specific question if a value is selected
    if (value !== '') {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`gad7_${index}`];
        return updatedErrors;
      });
    }
  };

  // Handle the follow-up "Do you have a plan?" question
  const handlePlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setAssessment((prev: AssessmentState) => ({
      ...prev,
      hasPlan: value,
      // Clear plan details if "no" is selected
      planDetails: value === 'no' ? '' : prev.planDetails
    }));

    // If user selects "yes" to having a plan, show crisis modal immediately
    if (value === 'yes') {
      setShowCrisisModal(true);
    }
  };

  // Handle the "What are the details of the plan?" question
  const handlePlanDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setAssessment((prev: AssessmentState) => ({
      ...prev,
      planDetails: value
    }));
  };


  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate required text fields
    if (!assessment.generalConcerns.trim()) {
      newErrors.generalConcerns = 'General concerns is required';
    }
    if (!assessment.concernsDuration.trim()) {
      newErrors.concernsDuration = 'Concerns duration is required';
    }
    if (!assessment.mainObjectives.trim()) {
      newErrors.mainObjectives = 'Main objectives is required';
    }

    // Validate arrays
    if (!assessment.availability.length) {
      newErrors.availability = 'At least one availability option must be selected';
    }
    if (!assessment.sessionTypes.length) {
      newErrors.sessionTypes = 'At least one session type must be selected';
    }

    // Validate PHQ9 scores
    assessment.phq9Scores.forEach((score, index) => {
      if (score === undefined || score === null) {
        newErrors[`phq9_${index}`] = 'Please select an option';
      }
    });

    // Validate GAD7 scores
    assessment.gad7Scores.forEach((score, index) => {
      if (score === undefined || score === null) {
        newErrors[`gad7_${index}`] = 'Please select an option';
      }
    });

    // CLIENT-02 (Phase 4): the self-harm plan follow-up is mandatory when
    // PHQ-9 item 9 scores above zero. A 'yes' answer is handled before
    // validation (crisis signposting modal); this blocks the silent-skip
    // case where the question was never answered.
    if ((assessment.phq9Scores[8] ?? 0) > 0 && !assessment.hasPlan) {
      newErrors.hasPlan = 'Please answer whether the client has a plan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCrisisSignpost = async () => {
    if (!assessorNotes.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide notes about your conversation with the client for the audit trail.',
        severity: 'warning'
      });
      return;
    }

    setIsSendingSignpost(true);
    try {
      await api.post(`/api/clients/${clientId}/signpost-crisis`, {
        assessorNotes: assessorNotes.trim(),
        planDetails: assessment.planDetails?.trim() || undefined
      });
      setSnackbar({
        open: true,
        message: 'Crisis support resources have been sent to the client via email. The assessment has been stopped and the client record has been retained for audit purposes.',
        severity: 'success'
      });
      // Navigate back to client details after a brief delay
      setTimeout(() => {
        navigate(`/client-details/${clientId}`);
      }, 2000);
    } catch (error) {
      console.error('Error signposting client:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send crisis support email. Please contact the client directly.',
        severity: 'error'
      });
    } finally {
      setIsSendingSignpost(false);
      setShowCrisisModal(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Block submission if client has a plan
    if (assessment.hasPlan === 'yes') {
      setShowCrisisModal(true);
      return;
    }

    if (!validateForm()) {
      // Find first error field and scroll to it
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before submitting.',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    setShowRetryButton(false);

    console.log('Current PHQ9 answers:', assessment.phq9Scores);
    console.log('Current GAD7 answers:', assessment.gad7Scores);

    try {
      // Calculate scores
      const phq9Score = assessment.phq9Scores.reduce((sum: number, score: number | undefined) => sum + (score ?? 0), 0);
      const gad7Score = assessment.gad7Scores.reduce((sum: number, score: number | undefined) => sum + (score ?? 0), 0);
      const totalScore = phq9Score + gad7Score;

      const assessmentData = {
        assessment: {
          phq9: assessment.phq9Scores,
          gad7: assessment.gad7Scores,
          concernsDuration: assessment.concernsDuration,
          mainObjectives: assessment.mainObjectives,
          personalPreferences: assessment.personalPreferences,
          generalConcerns: assessment.generalConcerns,
          previousCounsellor: assessment.previousCounsellor,
          officialDiagnoses: assessment.officialDiagnoses,
          medication: assessment.medication,
          recreationalDrugs: assessment.recreationalDrugs,
          alcoholUse: assessment.alcoholUse,
          substanceSupport: assessment.substanceSupport,
          personalityDisorder: assessment.personalityDisorder,
          otherSupport: assessment.otherSupport,
        },
        sessionTypes: assessment.sessionTypes,
        availability: assessment.availability,
        availabilitySlots: availabilitySlots,
        notes: assessment.notes,
        phq9Score: phq9Score,
        gad7Score: gad7Score,
        assessmentScore: totalScore,
        assessmentStatus: 'completed',
        assessed: true,
        initialAssessment: clientName.initialAssessment,
        hasPlan: assessment.hasPlan,
        planDetails: assessment.planDetails,
        difficultyLevelPHQ9: assessment.difficultyLevelPHQ9,
        difficultyLevelGAD7: assessment.difficultyLevelGAD7
      };

      // Save assessment data
      const response = await api.put(`/api/clients/${clientId}/assessment`, assessmentData);

      if (response.status === 200) {
        try {
          // Generate and issue terms and conditions
          await api.post(`/api/contract/generate-pdf/${clientId}`);
          // Clear draft on successful submission
          if (clientId) {
            await deleteAssessmentDraft(clientId, 'part2');
          }
          setSnackbar({
            open: true,
            message: 'Assessment completed and terms issued successfully!',
            severity: 'success'
          });
          setTimeout(() => {
            navigate(`/client-details/${clientId}`);
          }, 2000);
        } catch (error: any) {
          console.error('Error generating terms:', error);
          const errorMessage = error.response?.data?.message 
            || error.message 
            || 'Failed to generate contract terms.';
          setSnackbar({
            open: true,
            message: `Assessment saved successfully, but ${errorMessage} You can generate terms from the client details page.`,
            severity: 'warning'
          });
          setShowRetryButton(true);
          setIsSubmitting(false);
          return; // Don't navigate, let user retry
        }
      }
    } catch (error: any) {
      console.error('Error submitting assessment:', error);
      let errorMessage = 'Failed to submit assessment. Please try again.';
      
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.message 
          || error.response.data?.error 
          || `Server error (${error.response.status}). Please try again.`;
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryContractGeneration = async () => {
    setIsSubmitting(true);
    setShowRetryButton(false);
    try {
      await api.post(`/api/contract/generate-pdf/${clientId}`);
      setSnackbar({
        open: true,
        message: 'Contract terms generated successfully!',
        severity: 'success'
      });
      setTimeout(() => {
        navigate(`/client-details/${clientId}`);
      }, 2000);
    } catch (error: any) {
      console.error('Error generating terms:', error);
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Failed to generate contract terms.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setShowRetryButton(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestoreDraft = () => {
    if (savedDraft) {
      isRestoringDraft.current = true;
      setAssessment(savedDraft);
      setShowRestoreDraftDialog(false);
      setSnackbar({
        open: true,
        message: 'Draft restored successfully! Your previous work has been recovered.',
        severity: 'success'
      });
      // Reset flag after a delay
      setTimeout(() => {
        isRestoringDraft.current = false;
      }, 2000);
    }
  };

  const handleDiscardDraft = () => {
    if (clientId) {
      deleteAssessmentDraft(clientId, 'part2').catch((error) => console.error('Error discarding draft:', error));
    }
    setShowRestoreDraftDialog(false);
    setSavedDraft(null);
    setSnackbar({
      open: true,
      message: 'Draft discarded. Starting with fresh form.',
      severity: 'info'
    });
  };

  const renderError = (fieldName: string) => {
    return errors[fieldName] ? (
      <span className="text-red-500 text-sm mt-1">{errors[fieldName]}</span>
    ) : null;
  };

  // Multi-selects use BOTH onClick and onChange: React's change tracker on
  // <select multiple> only watches select.value (the first selected option),
  // so Ctrl/Cmd-clicking extra options can silently skip onChange in Chrome.
  // IMPORTANT: read e.currentTarget (the <select>), not e.target — the click
  // target is the clicked <option>, which has no .options property, so the
  // handler threw and the extra selections were reset on the next re-render.
  const handleMultiSelect = (field: 'availability' | 'sessionTypes') =>
    (e: React.SyntheticEvent<HTMLSelectElement>) => {
      const selectedOptions = Array.from(e.currentTarget.options)
        .filter(option => option.selected)
        .map(option => option.value);
      setAssessment((prev: AssessmentState) => ({
        ...prev,
        [field]: selectedOptions
      }));
      if (selectedOptions.length > 0) {
        setErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[field];
          return updatedErrors;
        });
      }
    };

  // Add follow-up questions for PHQ-9 question 9
  const renderPhq9FollowUp = () => {
    if (assessment.phq9Scores[8] && assessment.phq9Scores[8] > 0) {
      return (
        <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-yellow-800">
              Do you have a plan?
            </label>
            <select
              value={assessment.hasPlan}
              onChange={handlePlanChange}
              className="w-full p-2 border border-yellow-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
            >
              <option value="">Please select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {renderError('hasPlan')}
          </div>

          {assessment.hasPlan === 'yes' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-yellow-800">
                What are the details of the plan?
              </label>
              <textarea
                value={assessment.planDetails}
                onChange={handlePlanDetailsChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                rows={3}
              />
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <ThemeBackground variant="full">
      {/* Crisis Signposting Modal */}
      <Dialog 
        open={showCrisisModal} 
        onClose={() => setShowCrisisModal(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#dc3545', color: 'white', fontWeight: 700 }}>
          ⚠️ Immediate Support Required
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <div style={{ padding: '20px 0' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#dc3545' }}>
              We are not in a position to provide support at this time
            </Typography>
            
            <Typography paragraph sx={{ fontSize: '16px', lineHeight: 1.6, mt: 2 }}>
              Based on the information shared during this assessment, we believe you would benefit from 
              more immediate and specialized support than we can currently provide.
            </Typography>

            <div style={{ backgroundColor: '#dc3545', color: 'white', padding: '20px', borderRadius: '8px', margin: '20px 0' }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                If you are in immediate danger:
              </Typography>
              <ul style={{ fontSize: '16px', lineHeight: 1.8, paddingLeft: '20px' }}>
                <li><strong>Call 999</strong> for emergency services</li>
                <li>Go to your nearest <strong>A&E department</strong></li>
                <li>Contact your <strong>GP surgery</strong> immediately</li>
              </ul>
            </div>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
              24/7 Crisis Support Services:
            </Typography>

            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#007bff' }}>Samaritans</Typography>
              <Typography variant="body2"><strong>Phone:</strong> 116 123 (Free, 24/7)</Typography>
              <Typography variant="body2"><strong>Email:</strong> jo@samaritans.org</Typography>
            </div>

            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#28a745' }}>Crisis Text Line (Shout)</Typography>
              <Typography variant="body2"><strong>Text:</strong> SHOUT to 85258 (Free, 24/7)</Typography>
            </div>

            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#6f42c1' }}>NHS 111</Typography>
              <Typography variant="body2"><strong>Phone:</strong> 111 (Select mental health option)</Typography>
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666' }}>24/7 urgent mental health support</Typography>
            </div>

            <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', marginTop: '20px', border: '1px solid #ffc107' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#856404' }}>
                ✓ Your assessment record has been retained for audit purposes<br/>
                ✓ A comprehensive list of crisis support resources will be emailed to you<br/>
                ✓ You can contact us again in the future if you feel ready for counselling support
              </Typography>
            </div>

            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #dee2e6' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#495057' }}>
                Assessor Notes (Required for Audit Trail)
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Please document the conversation you had with the client, including any immediate safety concerns discussed.
                This information will be retained as part of the audit trail.
              </Typography>
              <textarea
                value={assessorNotes}
                onChange={(e) => setAssessorNotes(e.target.value)}
                placeholder="Document your conversation with the client, safety concerns discussed, and any immediate actions taken..."
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ced4da',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007bff'}
                onBlur={(e) => e.target.style.borderColor = '#ced4da'}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={() => {
              setShowCrisisModal(false);
              setAssessment(prev => ({ ...prev, hasPlan: '' }));
            }}
            variant="outlined"
            disabled={isSendingSignpost}
            sx={{
              borderColor: '#6c757d',
              color: '#6c757d',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#5a6268',
                backgroundColor: 'rgba(108, 117, 125, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCrisisSignpost}
            variant="contained"
            disabled={isSendingSignpost}
            sx={{
              backgroundColor: '#dc3545',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#c82333'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            {isSendingSignpost ? 'Sending Resources...' : 'Send Crisis Support Resources & Exit Assessment'}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                        }
                      }}
                      onClick={() => navigate(`/assessment-part-1/${clientId}`)}
                    >
                      Back to Part 1
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Assessment Part 2
                  </Typography>
                  {clientName && (
                    <Typography 
                      variant="subtitle1"
                      className="theme-text-secondary"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      {clientName.firstName} {clientName.lastName}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Error Summary Panel */}
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Please fix the following errors:</AlertTitle>
              <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field} style={{ marginBottom: '4px' }}>{message}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Main Form Section */}
          <div className="mt-8 space-y-6">
            {/* Counselling Information */}
            <div className="glass-card-light p-6 rounded-lg shadow">
              <Typography 
                variant="h5" 
                component="h2"
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 700,
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: '1.5rem'
                }}
              >
                Counselling Information
              </Typography>
              
              {/* Referral Type and Initial Assessment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Referral Type</label>
                  <input
                    type="text"
                    value={clientName?.referralTypeDisplayName || ''}
                    readOnly
                    className="mt-1 block w-full bg-white/90 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Initial Assessment</label>
                  <input
                    type="text"
                    value={clientName?.initialAssessment || ''}
                    readOnly
                    className="mt-1 block w-full bg-white/90 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-gray-900"
                  />
                </div>
              </div>

              {/* General Concerns */}
              <div className="mb-6">
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  General Concerns *
                </label>
                <textarea
                  name="generalConcerns"
                  value={assessment.generalConcerns}
                  onChange={handleChange}
                  className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white/90 text-gray-900 ${
                    errors.generalConcerns ? 'border-red-500' : ''
                  }`}
                  rows={4}
                />
                {renderError('generalConcerns')}
              </div>

              {/* Concerns Duration */}
              <div className="mb-6">
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  How long have you had these concerns? *
                </label>
                <textarea
                  name="concernsDuration"
                  value={assessment.concernsDuration}
                  onChange={handleChange}
                  className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white/90 text-gray-900 ${
                    errors.concernsDuration ? 'border-red-500' : ''
                  }`}
                  rows={3}
                />
                {renderError('concernsDuration')}
              </div>

              {/* Main Objectives */}
              <div className="mb-6">
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  What do you want from counselling? *
                </label>
                <textarea
                  name="mainObjectives"
                  value={assessment.mainObjectives}
                  onChange={handleChange}
                  className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white/90 text-gray-900 ${
                    errors.mainObjectives ? 'border-red-500' : ''
                  }`}
                  rows={3}
                />
                {renderError('mainObjectives')}
              </div>

              {/* Personal Preferences */}
              <div className="mb-6">
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  Personal Preferences
                </label>
                <p className="text-xs theme-text-secondary mb-2">
                  e.g., Male, Female, Culturally Sensitive etc.
                </p>
                <textarea
                  name="personalPreferences"
                  value={assessment.personalPreferences}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-white/90 text-gray-900"
                  rows={2}
                  placeholder="Enter any preferences for your counsellor..."
                />
              </div>
            </div>

            {/* Session Preferences */}
            <div className="glass-card-light p-6 rounded-lg shadow">
              <Typography 
                variant="h5" 
                component="h2"
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 700,
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: '0.5rem'
                }}
              >
                Session Preferences
              </Typography>
              <Typography 
                variant="body2" 
                className="theme-text-primary"
                sx={{
                  opacity: 0.8,
                  marginBottom: '1.5rem'
                }}
              >
                Hold Ctrl (Windows) or Command (Mac) to select multiple options
              </Typography>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Availability *</label>
                  <select
                    multiple
                    size={5}
                    value={assessment.availability}
                    onClick={handleMultiSelect('availability')}
                    onChange={handleMultiSelect('availability')}
                    className={`theme-input ${
                      errors.availability ? 'border-red-500' : ''
                    }`}
                  >
                    {availabilityOptions.map((time: Lookup) => (
                      <option key={time._id} value={time._id}>
                        {time.displayName}
                      </option>
                    ))}
                  </select>
                  {renderError('availability')}
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Session Types *</label>
                  <select
                    multiple
                    size={5}
                    value={assessment.sessionTypes}
                    onClick={handleMultiSelect('sessionTypes')}
                    onChange={handleMultiSelect('sessionTypes')}
                    className={`theme-input ${
                      errors.sessionTypes ? 'border-red-500' : ''
                    }`}
                  >
                    {sessionTypes.length > 0 ? (
                      sessionTypes.map((type: Lookup) => (
                        <option key={type._id} value={type._id}>
                          {type.displayName}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>Loading session types...</option>
                    )}
                  </select>
                  {renderError('sessionTypes')}
                </div>
              </div>

              {/* Specific Time Slots */}
              <div className="mt-6">
                <label className="block text-sm font-medium theme-text-primary mb-2">
                  Specific Availability Time Slots (Optional)
                </label>
                <Typography 
                  variant="body2" 
                  className="theme-text-primary"
                  sx={{ opacity: 0.7, mb: 2 }}
                >
                  Add specific days and time ranges when the client is available
                </Typography>
                <AvailabilitySlotEditor
                  slots={availabilitySlots}
                  onChange={setAvailabilitySlots}
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="glass-card-light p-6 rounded-lg shadow">
              <Typography 
                variant="h5" 
                component="h2"
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 700,
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: '1.5rem'
                }}
              >
                Additional Information
              </Typography>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Previous Counsellor & Official Diagnoses */}
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Previous Counsellor</label>
                  <input
                    type="text"
                    name="previousCounsellor"
                    value={assessment.previousCounsellor}
                    onChange={handleChange}
                    className="theme-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Official Diagnoses</label>
                  <textarea
                    name="officialDiagnoses"
                    value={assessment.officialDiagnoses}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>

                {/* Medication & Recreational Drugs */}
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Medication</label>
                  <textarea
                    name="medication"
                    value={assessment.medication}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Recreational Drugs</label>
                  <textarea
                    name="recreationalDrugs"
                    value={assessment.recreationalDrugs}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>

                {/* Alcohol Use & Substance Support */}
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Alcohol Use</label>
                  <textarea
                    name="alcoholUse"
                    value={assessment.alcoholUse}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Substance Support</label>
                  <textarea
                    name="substanceSupport"
                    value={assessment.substanceSupport}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>

                {/* Personality Disorder & Other Support */}
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Personality Disorder</label>
                  <textarea
                    name="personalityDisorder"
                    value={assessment.personalityDisorder}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary">Other Support</label>
                  <textarea
                    name="otherSupport"
                    value={assessment.otherSupport}
                    onChange={handleChange}
                    className="theme-input"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* PHQ-9 Assessment */}
            <div className="glass-card-light p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium theme-text-primary">PHQ-9 Assessment</h3>
              <div className="space-y-4">
                {phq9Questions.map((question, index) => (
                  <div key={index} className="space-y-2">
                    <label className="block text-sm font-medium theme-text-primary">
                      {question} *
                    </label>
                    <select
                      value={assessment.phq9Scores[index]}
                      onChange={(e) => handlePhq9Change(index, e.target.value)}
                      className={`theme-input ${
                        errors[`phq9_${index}`] ? 'border-red-500' : ''
                      }`}
                    >
                      {scoreOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {renderError(`phq9_${index}`)}
                  </div>
                ))}
                {renderPhq9FollowUp()}
              </div>
            </div>

            {/* GAD-7 Assessment */}
            <div className="glass-card-light p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium theme-text-primary">GAD-7 Assessment</h3>
              <div className="space-y-4">
                {gad7Questions.map((question, index) => (
                  <div key={index} className="space-y-2">
                    <label className="block text-sm font-medium theme-text-primary">
                      {question} *
                    </label>
                    <select
                      value={assessment.gad7Scores[index]}
                      onChange={(e) => handleGad7Change(index, e.target.value)}
                      className={`theme-input ${
                        errors[`gad7_${index}`] ? 'border-red-500' : ''
                      }`}
                    >
                      {scoreOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {renderError(`gad7_${index}`)}
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '160px',
                  backgroundColor: '#2196f3',
                  '&:hover': {
                    backgroundColor: '#1976d2'
                  }
                }}
                onClick={() => navigate(`/assessment-part-1/${clientId}`)}
              >
                Back to Part 1
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '160px',
                  backgroundColor: '#2196f3',
                  '&:hover': {
                    backgroundColor: '#1976d2'
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0'
                  }
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
              </Button>
              {showRetryButton && (
                <Button
                  variant="outlined"
                  onClick={handleRetryContractGeneration}
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: '160px',
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    '&:hover': {
                      borderColor: '#f57c00',
                      backgroundColor: 'rgba(255, 152, 0, 0.04)'
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  {isSubmitting ? 'Retrying...' : 'Retry Contract Generation'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Restore Draft Dialog */}
      <Dialog
        open={showRestoreDraftDialog}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
            Unsaved Work Found
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            We found unsaved assessment data from a previous session. Would you like to restore it?
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This draft was saved automatically to prevent data loss. You can:
          </Typography>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li style={{ marginBottom: '8px' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Restore:</strong> Continue where you left off with your previous work
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Discard:</strong> Start fresh with a blank form
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f9fafb' }}>
          <Button
            onClick={handleDiscardDraft}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#9ca3af',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#6b7280',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Discard Draft
          </Button>
          <Button
            onClick={handleRestoreDraft}
            variant="contained"
            autoFocus
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Restore Draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
}

export default AssessmentPart2;
